import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import type { VoiceMessage } from "@/hooks/voice-agent/types";

export function useConversation(siteId: string | undefined) {
  const [messages, setMessages] = useState<VoiceMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);

  // Load conversation history
  const loadConversation = useCallback(async () => {
    if (!siteId) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from("agent_conversations")
        .select("id, messages")
        .eq("site_id", siteId)
        .eq("user_id", user.id)
        .maybeSingle();

      if (data) {
        setConversationId(data.id);
        const parsedMessages = Array.isArray(data.messages)
          ? (data.messages as unknown as VoiceMessage[]).slice(-50)
          : [];
        setMessages(parsedMessages);
        logger.info("useVoiceAgent", "Loaded conversation", { count: parsedMessages.length });
      }
    } catch (error) {
      logger.error("useVoiceAgent", "Error loading conversation", error);
    }
  }, [siteId]);

  // Save messages to database
  const saveMessages = useCallback(async (messagesToSave: VoiceMessage[]) => {
    if (!siteId || messagesToSave.length === 0) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const upsertData: any = {
        site_id: siteId,
        user_id: user.id,
        messages: messagesToSave,
        updated_at: new Date().toISOString(),
      };

      if (conversationId) {
        upsertData.id = conversationId;
      }

      const { data, error } = await supabase
        .from("agent_conversations")
        .upsert(upsertData, { onConflict: "site_id,user_id" })
        .select("id")
        .single();

      if (error) throw error;

      if (data && !conversationId) {
        setConversationId(data.id);
      }
    } catch (error) {
      logger.error("useVoiceAgent", "Error saving messages", error);
    }
  }, [siteId, conversationId]);

  return {
    messages,
    setMessages,
    conversationId,
    setConversationId,
    loadConversation,
    saveMessages,
  };
}
